import Type from "../Types";
import { createFillPattern, showToast } from "../Utility";
import * as Cesium from "cesium";

export const loadGeoJSONToCesium = async ({ viewer, geojson, options = {}, callback = null, geojsonType, layerName = "", id = null, invisible = false }) => {
  const settings = { ...options };

  if (!viewer || !viewer.dataSources) {
    showToast("Viewer is not available.", Type.NOTIFICATION.ERROR);
    return null;
  }

  const safeCssColor = (css, fallback = Cesium.Color.WHITE) => {
    try {
      if (typeof css === "string" && css.trim()) {
        return Cesium.Color.fromCssColorString(css);
      }
    } catch (_) {}
    return fallback;
  };

  const originalWasString = typeof geojson === "string";
  let dataToLoad = geojson;
  if (originalWasString) {
    try {
      dataToLoad = JSON.parse(geojson);
    } catch (error) {
      console.warn(error);
      showToast("Invalid JSON: failed to parse GeoJSON text.", Type.NOTIFICATION.ERROR);
      return null;
    }
  }
  if (!dataToLoad || typeof dataToLoad !== "object") {
    showToast("GeoJSON is empty or not an object.", Type.NOTIFICATION.ERROR);
    return null;
  }
  const geojsonForCallback = originalWasString ? geojson : dataToLoad;

  // create datasource and set visibility according to `invisible` flag
  const dataSource = new Cesium.CustomDataSource(settings.dataSourceName || "GeoJSONLayer");
  dataSource.layerName = layerName;
  dataSource.layerId = id;

  // store priority metadata on datasource (kept from original)
  dataSource._priority = Number.isFinite(Number(settings.priority)) ? Number(settings.priority) : 0;
  dataSource._priorityOrder = typeof performance !== "undefined" ? performance.now() : Date.now();

  // IMPORTANT: use dataSource.show to hide/show entire datasource when invisible is true
  dataSource.show = !invisible;

  try {
    const addPoint = (coords, props) => {
      const [lon, lat, h = 0] = coords;
      const pos = Cesium.Cartesian3.fromDegrees(lon, lat, h);
      return dataSource.entities.add({
        position: pos,
        point: new Cesium.PointGraphics({
          color: safeCssColor(props?.outlineColor || settings.defaultOutlineColor, Cesium.Color.WHITE),
          pixelSize: Number.isFinite(Number(props?.strokeWidth)) ? Number(props.strokeWidth) : settings.defaultLineWidth || 6,
          outlineColor: safeCssColor(props?.outlineColor || settings.defaultOutlineColor, Cesium.Color.WHITE),
          outlineWidth: 1,
        }),
        properties: props || {},
      });
    };

    const addLineString = (coords, props) => {
      const positions = coords.map(([lon, lat, h = 0]) => Cesium.Cartesian3.fromDegrees(lon, lat, h));
      return dataSource.entities.add({
        polyline: new Cesium.PolylineGraphics({
          positions,
          width: Number.isFinite(Number(props?.strokeWidth)) ? Number(props.strokeWidth) : settings.defaultLineWidth || 3,
        }),
        properties: props || {},
      });
    };

    const addPolygon = (rings, props) => {
      const outer = (rings?.[0] || []).map(([lon, lat, h = 0]) => Cesium.Cartesian3.fromDegrees(lon, lat, h));
      return dataSource.entities.add({
        polygon: new Cesium.PolygonGraphics({
          hierarchy: new Cesium.PolygonHierarchy(outer),
          perPositionHeight: true,
        }),
        properties: props || {},
      });
    };

    for (const f of dataToLoad.features || []) {
      const g = f?.geometry;
      if (!g) continue;
      const props = f?.properties || {};

      let entity = null;
      switch (g.type) {
        case "Point":
          entity = addPoint(g.coordinates, props);
          break;
        case "MultiPoint":
          for (const c of g.coordinates) addPoint(c, props);
          break;
        case "LineString":
          entity = addLineString(g.coordinates, props);
          break;
        case "MultiLineString":
          for (const line of g.coordinates) addLineString(line, props);
          break;
        case "Polygon":
          entity = addPolygon(g.coordinates, props);
          break;
        case "MultiPolygon":
          for (const poly of g.coordinates) addPolygon(poly, props);
          break;
        default:
          break;
      }

      if (entity) entity.geojsonType = geojsonType;
    }
  } catch (err) {
    console.error("Failed to build entities from GeoJSON:", err);
    showToast("Failed to build entities from GeoJSON.", Type.NOTIFICATION.ERROR);
    return null;
  }

  const loadedDs = dataSource;

  const entities = loadedDs.entities.values;

  let styleFillColor = null;
  let styleOutlineColor = null;
  let styleStrokeWidth = null;
  let styleType = "solid";

  try {
    for (const entity of entities) {
      const tags = entity.properties?.getValue?.() || {};
      const defStyle = (settings.defaultStyle || "solid").toLowerCase();

      const normalizeTagColor = (value) => {
        if (!value) return null;
        const v = String(value).trim().toLowerCase();

        if (!v || v === "unknown" || v === "none" || v === "null") {
          return null;
        }

        return value;
      };

      styleFillColor = normalizeTagColor(tags.color) || settings.defaultFillColor;
      styleType = defStyle;

      if (["solid", "dotted", "dashed"].includes(tags?.subtype?.toLowerCase())) {
        styleType = tags.subtype;
      }

      styleOutlineColor = normalizeTagColor(tags.color) || tags.outlineColor || settings.defaultOutlineColor;
      styleStrokeWidth = tags.strokeWidth || settings.defaultLineWidth;

      const p = Number.isFinite(dataSource._priority) ? dataSource._priority : 0;

      // if (entity.billboard) {
      //   entity.billboard = undefined;
      // }
      if (entity.point) {
        entity.point.disableDepthTestDistance = Number.POSITIVE_INFINITY;
        entity.point.eyeOffset = new Cesium.Cartesian3(0, 0, p);
      }
      if (entity.label) {
        entity.label.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      }

      if (entity.polyline) {
        const color = safeCssColor(styleOutlineColor, Cesium.Color.YELLOW);
        entity.originalColor = color;
        entity.originalSubtype = styleType;
        entity.originalWidth = settings.defaultLineWidth;

        if (styleType.toLocaleLowerCase() === "dashed") {
          entity.polyline.material = new Cesium.PolylineDashMaterialProperty({
            color: color,
            dashLength: settings.dashedDashLength,
          });
        } else if (styleType.toLocaleLowerCase() === "dotted") {
          entity.polyline.material = new Cesium.PolylineDashMaterialProperty({
            color: color,
            dashLength: 5,
          });
        } else if (styleType.toLocaleLowerCase() === "pattern") {
          entity.polyline.material = new Cesium.PolylineDashMaterialProperty({
            color,
            dashLength: 12,
            dashPattern: parseInt(settings.dottedDashLength, 2),
          });
        } else {
          entity.polyline.material = new Cesium.ColorMaterialProperty(color);
        }
        entity.polyline.width = settings.defaultLineWidth;
        entity.polyline.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      }

      // Polygon
      if (entity.polygon) {
        // prefer zIndex if supported by your Cesium version
        if ("zIndex" in entity.polygon) {
          // entity.polygon.zIndex = p;
        } else {
          // fallback: tiny height offset to break depth ties
          entity.polygon.height = p * 0.1; // meters
          entity.polygon.perPositionHeight = true;
        }

        if (settings.defaultPattren) {
          entity.polygon.material = await createFillPattern(settings.defaultPattren, styleFillColor);
        } else {
          const fillColor = safeCssColor(settings.defaultFillColor, Cesium.Color.fromBytes(0, 153, 255, 160));
          const outlineColor = safeCssColor(settings.defaultOutlineColor, Cesium.Color.BLACK);
          entity.polygon.material = fillColor;
          entity.polygon.outlineColor = outlineColor;
        }
        entity.polygon.outline = true;
        entity.polygon.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      }

      entity.geojsonType = geojsonType;
    }
  } catch (styleErr) {
    console.error("Styling error:", styleErr);
    showToast("Styling failed for GeoJSON entities.", Type.NOTIFICATION.ERROR);
    return null;
  }

  if (typeof callback === "function") {
    try {
      callback(loadedDs, geojsonForCallback, null, styleFillColor || null, styleOutlineColor || null, styleStrokeWidth || null, styleType || null);
    } catch (cbErr) {
      console.error("Callback error:", cbErr);
      showToast("A callback error occurred.", Type.NOTIFICATION.WARNING);
    }
  }

  try {
    await viewer.dataSources.add(loadedDs);

    // sort existing datasources by priority and raise each to top (keeps rendering order consistent)
    const sources = viewer.dataSources._dataSources.slice().sort((a, b) => {
      const pa = Number.isFinite(a._priority) ? a._priority : 0;
      const pb = Number.isFinite(b._priority) ? b._priority : 0;
      if (pa !== pb) return pa - pb;
      const ta = Number.isFinite(a._priorityOrder) ? a._priorityOrder : 0;
      const tb = Number.isFinite(b._priorityOrder) ? b._priorityOrder : 0;

      return ta - tb;
    });

    for (const ds of sources) viewer.dataSources.raiseToTop(ds);

    if (entities.length > 0 && settings.zoomToDataSource) {
      try {
        await viewer.zoomTo(loadedDs);

        // clamp min camera height so it won't zoom in closer than 25m
        const minHeight = 25;
        const cam = viewer.camera;

        const carto = Cesium.Cartographic.fromCartesian(cam.positionWC);
        if (carto.height < minHeight) {
          carto.height = minHeight;
          cam.setView({
            destination: Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, carto.height),
            orientation: {
              heading: cam.heading,
              pitch: cam.pitch,
              roll: cam.roll,
            },
          });
        }
      } catch (_) {}
    }

    return loadedDs;
  } catch (attachErr) {
    console.error("Failed to attach datasource:", attachErr);
    showToast("Failed to attach GeoJSON datasource to the viewer.", Type.NOTIFICATION.ERROR);
    return null;
  }
};
